import { GenericTradeParams, TpSlValueState } from '@ui/features/Trade/types';
import { useTradePairInfosByPairBase } from '@ui/hooks/trade/useTradePairInfos';
import { useTokenDisplayDecimals } from '@ui/hooks/useTokenDisplayDecimals';
import {
  getActiveSlOrders,
  getActiveTpOrders,
  getDecreaseOrderTriggerPrice,
} from '@ui/services/contract/hooks/TradingPortalFacet/useDecreaseOrders/selectors';
import type { DecreaseOrderRow } from '@ui/services/contract/hooks/TradingPortalFacet/useDecreaseOrders/types';
import { PossibleBigNumber } from '@ui/types/utility';
import { isNil } from 'lodash-es';
import { useCallback } from 'react';

export const useSyncTpSl = ({
  genericTradeParams,
  activeDecreaseOrders,
  setTp,
  setSl,
}: {
  setTp: (value: TpSlValueState) => void;
  setSl: (value: TpSlValueState) => void;
  activeDecreaseOrders?: DecreaseOrderRow[];
} & GenericTradeParams) => {
  const priceDisplayDecimals = useTokenDisplayDecimals(
    genericTradeParams?.pair.baseSymbol,
    genericTradeParams?.pair.priceDisplayDecimals,
  );

  const tradePair = useTradePairInfosByPairBase(
    genericTradeParams.pair.address,
  );

  return useCallback(
    ({
      _collateralTokenMarketPrice,
      _pairMarketPrice,
    }: {
      _collateralTokenMarketPrice: PossibleBigNumber;
      _pairMarketPrice: PossibleBigNumber;
    }) => {
      if (
        isNil(_collateralTokenMarketPrice) ||
        isNil(_pairMarketPrice) ||
        isNil(tradePair)
      )
        return;

      const activeTpPrice =
        getDecreaseOrderTriggerPrice(getActiveTpOrders(activeDecreaseOrders)[0]) ??
        genericTradeParams.takeProfit;
      const activeSlPrice =
        getDecreaseOrderTriggerPrice(getActiveSlOrders(activeDecreaseOrders)[0]) ??
        genericTradeParams.stopLoss;

      setTp({
        priceValue: activeTpPrice.toFixed(priceDisplayDecimals),
        pnlPercentage: genericTradeParams.getTakeProfitPercentage({
          collateralTokenMarketPrice: _collateralTokenMarketPrice,
          pairMarketPrice: _pairMarketPrice,
          tradePair,
        }),
        deltaPnl: genericTradeParams.getTakeProfitDeltaPnl({
          collateralTokenMarketPrice: _collateralTokenMarketPrice,
          pairMarketPrice: _pairMarketPrice,
          tradePair,
        }),
        isPnlPercentage: false,
        isLong: genericTradeParams.isLong,
      });

      if (!activeSlPrice.isZero()) {
        setSl({
          priceValue: activeSlPrice.toFixed(priceDisplayDecimals),
          pnlPercentage: genericTradeParams.getStopLossPercentage({
            collateralTokenMarketPrice: _collateralTokenMarketPrice,
            pairMarketPrice: _pairMarketPrice,
            tradePair,
          }),
          deltaPnl: genericTradeParams.getStopLossDeltaPnl({
            collateralTokenMarketPrice: _collateralTokenMarketPrice,
            pairMarketPrice: _pairMarketPrice,
            tradePair,
          }),
          isPnlPercentage: false,
          isLong: genericTradeParams.isLong,
        });
      } else {
        setSl({
          // back to init
          priceValue: '',
          pnlPercentage: undefined,
          deltaPnl: undefined,
          isPnlPercentage: undefined,
          isLong: undefined,
        });
      }
    },
    [
      activeDecreaseOrders,
      genericTradeParams,
      priceDisplayDecimals,
      setSl,
      setTp,
      tradePair,
    ],
  );
};
